import { z } from 'zod';
/**
 * Every domain enum of plan §4, declared once so later phases add *areas* and never enum values that
 * two files then disagree about. They are persisted as `varchar` + `$type<>()` (never pg enums), so the
 * service-side unions in `src/app/domain` mirror these exactly.
 *
 * Phase 0 uses only a handful of them; the rest are here because the data model is settled and the
 * engine, the estimator and the inventory pages all read from this list.
 */
/** Membership role inside a tenant. ADMIN is SALES plus equations, templates, users and approvals. */
export const userRoleSchema = z.enum(['ADMIN', 'SALES']);
/** A disabled user cannot sign in and loses every live session on the next refresh (plan §2.3). */
export const userStatusSchema = z.enum(['ACTIVE', 'DISABLED']);
/** A membership is disabled per tenant without touching the user account itself. */
export const membershipStatusSchema = z.enum(['ACTIVE', 'DISABLED']);
export const tenantStatusSchema = z.enum(['ACTIVE', 'DISABLED']);
/** Policy and template versions: immutable once published (plan §3.4). */
export const versionStatusSchema = z.enum(['DRAFT', 'PUBLISHED', 'ARCHIVED']);
export const templateStatusSchema = z.enum(['ACTIVE', 'ARCHIVED']);
/** The estimate lifecycle of plan §3.1 — an opportunity, never stock. */
export const estimateStatusSchema = z.enum(['DRAFT', 'READY_TO_OFFER', 'OFFER_SHARED', 'DEAL_AGREED', 'NO_DEAL']);
/** Inventory exists only after a confirmed purchase: `IN_WAREHOUSE ⇄ IN_SERVICE → SOLD`. */
export const inventoryStatusSchema = z.enum(['IN_WAREHOUSE', 'IN_SERVICE', 'SOLD']);
/** Inspections, valuations, cost items, approvals and activity hang off either owner. */
export const ownerTypeSchema = z.enum(['ESTIMATE', 'VEHICLE']);
export const acquisitionTypeSchema = z.enum(['DIRECT', 'TRADE_IN']);
export const fuelSchema = z.enum(['GAS', 'HYBRID', 'EV', 'DIESEL']);
export const originSchema = z.enum(['AGENCY', 'GCC', 'IMPORTED', 'OTHER']);
export const usageSchema = z.enum(['PRIVATE', 'FLEET', 'RENTAL', 'TAXI']);
/** The سبعة جيد grade. `UNKNOWN` is never treated as `G7` (PP AC03). */
export const grade7Schema = z.enum(['UNKNOWN', 'G7', 'G4', 'G3', 'G2', 'G1', 'G0']);
/** Per-area condition severity. `UNKNOWN` is never treated as `EXCELLENT` (PP AC03). */
export const severitySchema = z.enum(['UNKNOWN', 'EXCELLENT', 'MINOR', 'MODERATE', 'MAJOR', 'SEVERE']);
export const serviceHistorySchema = z.enum(['UNKNOWN', 'AGENCY_FULL', 'THIRD_PARTY_AGENCY', 'THIRD_PARTY_DOCUMENTED', 'PARTIAL', 'NONE']);
export const profitModeSchema = z.enum(['FIXED', 'MARGIN']);
/** The four quick-range scenarios: condition × mileage band (plan §5.11). */
export const quickCategorySchema = z.enum(['GOOD_LOW', 'GOOD_HIGH', 'POOR_LOW', 'POOR_HIGH']);
export const valuationStatusSchema = z.enum(['SAVED', 'SUPERSEDED']);
export const valuationTriggerSchema = z.enum(['MANUAL', 'OFFER_SHARED', 'PURCHASE', 'COPIED', 'SALE', 'CORRECTION']);
export const costCategorySchema = z.enum([
    'RECONDITIONING',
    'LICENSING',
    'GENERAL_FEES',
    'REGISTRATION',
    'TRANSFER',
    'INSURANCE',
    'COMMISSION',
    'FUEL',
    'OVERHEAD',
    'TAX_TRAFFIC',
    'OTHER',
]);
export const costStatusSchema = z.enum(['ESTIMATED', 'IN_PROGRESS', 'DONE', 'WAIVED']);
export const workOptionSchema = z.enum(['MINOR_BODY', 'MEDIUM_BODY', 'MAJOR_BODY', 'BATTERY', 'OIL_SERVICE', 'MANUAL']);
export const docCategorySchema = z.enum([
    'PHOTO_FRONT',
    'PHOTO_REAR',
    'PHOTO_LEFT',
    'PHOTO_RIGHT',
    'PHOTO_INTERIOR',
    'PHOTO_DAMAGE',
    'LICENSE',
    'EXTERNAL_REPORT',
    'INVOICE',
    'OTHER',
]);
/**
 * The work category of a **historical** service event (plan §10 phase 5, from PP §8's suggested
 * classification). It is deliberately its own list rather than a reuse of `costCategory`: a cost
 * category answers "which fee is this?" for money CTA is about to spend, and this one answers "what was
 * done to the car before we owned it?" — evidence, never a figure (PP AC07).
 */
export const serviceCategorySchema = z.enum([
    'ROUTINE',
    'WEAR_AND_TEAR',
    'MECHANICAL_REPAIR',
    'MAJOR_POWERTRAIN',
    'HYBRID_EV',
    'MINOR_BODY',
    'MAJOR_BODY',
    'SRS_AIRBAG',
    'OTHER',
]);
export const approvalTypeSchema = z.enum(['ABOVE_CEILING', 'CORRECTION']);
export const approvalDecisionSchema = z.enum(['PENDING', 'APPROVED', 'REJECTED']);
export const activityKindSchema = z.enum([
    'ESTIMATE_CREATED',
    'FIELD_CHANGE',
    'TEMPLATE_APPLIED',
    'QUICK_CATEGORY',
    'VALUATION_SAVED',
    'OFFER_SHARED',
    'NO_DEAL',
    'REOPENED',
    'APPROVAL',
    'PURCHASE',
    'STATUS_CHANGE',
    'SALE',
    'COST',
    'DOCUMENT',
    'HISTORY',
    'CORRECTION',
    'TEMPLATE_PUBLISHED',
    'POLICY_PUBLISHED',
    'MEMBERSHIP',
    // A template draft written by something other than its own editor: a starter preset applied to it, or
    // a new last price sent from the estimator (plan §11.19).
    'TEMPLATE_DRAFT_UPDATED',
    'PRESET_UPDATED',
]);
export const demandLevelSchema = z.enum(['HIGH', 'MEDIUM', 'LOW']);
export const stockLevelSchema = z.enum(['LOW', 'MEDIUM', 'HIGH']);
/** SYSTEM equation ids are stable and undeletable; CUSTOM ids are the `x_` intermediates (plan §5.1). */
export const equationKindSchema = z.enum(['SYSTEM', 'CUSTOM']);
/** `TRADE_IN`-scoped equations are skipped for a direct purchase (plan §5.1). */
export const equationScopeSchema = z.enum(['ALWAYS', 'TRADE_IN']);
//# sourceMappingURL=enum.schemas.js.map